import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Button } from "../components/ui/button";
import { Building2, Package, FileText, FileSignature, Wrench, Key, Users, TrendingUp, Clock, ArrowUpRight, DoorOpen, Calendar, Bell } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [scheduledServices, setScheduledServices] = useState([]);
  const [upcomingReminders, setUpcomingReminders] = useState([]);
  const [reminderDays, setReminderDays] = useState(7);
  const [dashboardSettings, setDashboardSettings] = useState({ dashboard_title: "", dashboard_subtitle: "" });
  const [loading, setLoading] = useState(true);

  useEffect(function() { 
    fetchStats(); 
    fetchDashboardSettings();
    fetchScheduledServices();
  }, []);

  useEffect(function() {
    fetchUpcomingReminders();
  }, [reminderDays]);

  async function fetchStats() {
    try {
      var response = await fetch(API_URL + "/api/dashboard/stats", { credentials: "include", headers: token ? { Authorization: "Bearer " + token } : {} });
      if (response.ok) setStats(await response.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  async function fetchDashboardSettings() {
    try {
      var response = await fetch(API_URL + "/api/settings/dashboard", { credentials: "include", headers: token ? { Authorization: "Bearer " + token } : {} });
      if (response.ok) {
        var data = await response.json();
        setDashboardSettings(data);
      }
    } catch (err) { console.error(err); }
  }

  async function fetchScheduledServices() {
    try {
      var response = await fetch(API_URL + "/api/dashboard/scheduled-services", { credentials: "include", headers: token ? { Authorization: "Bearer " + token } : {} });
      if (response.ok) {
        var data = await response.json();
        setScheduledServices(data);
      }
    } catch (err) { console.error(err); }
  }

  async function fetchUpcomingReminders() {
    try {
      var response = await fetch(API_URL + "/api/reminders/upcoming?days=" + reminderDays, { credentials: "include", headers: token ? { Authorization: "Bearer " + token } : {} });
      if (response.ok) {
        var data = await response.json();
        setUpcomingReminders(data);
      }
    } catch (err) { console.error(err); }
  }

  function formatTimestamp(ts) { if (!ts) return ""; return new Date(ts).toLocaleString(); }

  function formatDate(dateStr) { 
    if (!dateStr) return ""; 
    var date = new Date(dateStr);
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  function getDaysUntil(dateStr) {
    if (!dateStr) return null;
    var today = new Date();
    today.setHours(0, 0, 0, 0);
    var target = new Date(dateStr);
    target.setHours(0, 0, 0, 0);
    var diff = Math.ceil((target - today) / (1000 * 60 * 60 * 24));
    return diff;
  }

  function getActionColor(action) {
    if (action === "create") return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
    if (action === "update") return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
    if (action === "delete") return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
    if (action === "login") return "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400";
    return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
  }

  function getUrgencyBadge(daysUntil) {
    if (daysUntil === null) return null;
    if (daysUntil <= 0) return <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">{t("dashboard.today")}</Badge>;
    if (daysUntil <= 3) return <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400">{daysUntil} {t("dashboard.daysLeft")}</Badge>;
    if (daysUntil <= 7) return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">{daysUntil} {t("dashboard.daysLeft")}</Badge>;
    return <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">{daysUntil} {t("dashboard.daysLeft")}</Badge>;
  }

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  var propsTotal = stats?.properties?.total || 0;
  var propsAvailable = stats?.properties?.available || 0;
  var propsRented = stats?.properties?.rented || 0;
  var propsMaintenance = stats?.properties?.maintenance || 0;
  var unitsTotal = stats?.units?.total || 0;
  var unitsAvailable = stats?.units?.available || 0;
  var unitsRented = stats?.units?.rented || 0;
  var assetsCount = stats?.assets || 0;
  var docsCount = stats?.documents || 0;
  var contractsCount = stats?.contracts || 0;
  var servicesPending = stats?.services_pending || 0;
  var rentalsActive = stats?.active_rentals || 0;
  var keysCount = stats?.keys || 0;
  var recentActivity = stats?.recent_activity || [];

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{dashboardSettings.dashboard_title || t("dashboard.title")}</h1>
          <p className="text-muted-foreground mt-1">{dashboardSettings.dashboard_subtitle || t("dashboard.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Clock className="w-4 h-4" />{t("dashboard.lastUpdated")}: {new Date().toLocaleTimeString()}</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-8 gap-4">
        <Link to="/properties" className="stat-card group hover:border-primary/50 transition-colors" data-testid="stat-total-properties">
          <div className="flex items-center justify-between mb-3"><div className="p-2 rounded-lg bg-blue-500/10"><Building2 className="w-4 h-4 text-blue-500" /></div><ArrowUpRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" /></div>
          <div className="text-2xl font-bold">{propsTotal}</div><div className="text-xs text-muted-foreground mt-1">{t("dashboard.totalProperties")}</div>
        </Link>
        <Link to="/properties" className="stat-card group hover:border-primary/50 transition-colors" data-testid="stat-total-units">
          <div className="flex items-center justify-between mb-3"><div className="p-2 rounded-lg bg-cyan-500/10"><DoorOpen className="w-4 h-4 text-cyan-500" /></div><ArrowUpRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" /></div>
          <div className="text-2xl font-bold">{unitsTotal}</div><div className="text-xs text-muted-foreground mt-1">{t("dashboard.totalUnits")}</div>
        </Link>
        <Link to="/assets" className="stat-card group hover:border-primary/50 transition-colors" data-testid="stat-total-assets">
          <div className="flex items-center justify-between mb-3"><div className="p-2 rounded-lg bg-purple-500/10"><Package className="w-4 h-4 text-purple-500" /></div><ArrowUpRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" /></div>
          <div className="text-2xl font-bold">{assetsCount}</div><div className="text-xs text-muted-foreground mt-1">{t("dashboard.totalAssets")}</div>
        </Link>
        <Link to="/documents" className="stat-card group hover:border-primary/50 transition-colors" data-testid="stat-documents">
          <div className="flex items-center justify-between mb-3"><div className="p-2 rounded-lg bg-green-500/10"><FileText className="w-4 h-4 text-green-500" /></div><ArrowUpRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" /></div>
          <div className="text-2xl font-bold">{docsCount}</div><div className="text-xs text-muted-foreground mt-1">{t("dashboard.documents")}</div>
        </Link>
        <Link to="/contracts" className="stat-card group hover:border-primary/50 transition-colors" data-testid="stat-contracts">
          <div className="flex items-center justify-between mb-3"><div className="p-2 rounded-lg bg-indigo-500/10"><FileSignature className="w-4 h-4 text-indigo-500" /></div><ArrowUpRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" /></div>
          <div className="text-2xl font-bold">{contractsCount}</div><div className="text-xs text-muted-foreground mt-1">{t("dashboard.contracts")}</div>
        </Link>
        <Link to="/services" className="stat-card group hover:border-primary/50 transition-colors" data-testid="stat-pending-services">
          <div className="flex items-center justify-between mb-3"><div className="p-2 rounded-lg bg-orange-500/10"><Wrench className="w-4 h-4 text-orange-500" /></div><ArrowUpRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" /></div>
          <div className="text-2xl font-bold">{servicesPending}</div><div className="text-xs text-muted-foreground mt-1">{t("dashboard.pendingServices")}</div>
        </Link>
        <Link to="/rentals" className="stat-card group hover:border-primary/50 transition-colors" data-testid="stat-active-rentals">
          <div className="flex items-center justify-between mb-3"><div className="p-2 rounded-lg bg-teal-500/10"><Users className="w-4 h-4 text-teal-500" /></div><ArrowUpRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" /></div>
          <div className="text-2xl font-bold">{rentalsActive}</div><div className="text-xs text-muted-foreground mt-1">{t("dashboard.activeRentals")}</div>
        </Link>
        <Link to="/keys" className="stat-card group hover:border-primary/50 transition-colors" data-testid="stat-keys-tracked">
          <div className="flex items-center justify-between mb-3"><div className="p-2 rounded-lg bg-amber-500/10"><Key className="w-4 h-4 text-amber-500" /></div><ArrowUpRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" /></div>
          <div className="text-2xl font-bold">{keysCount}</div><div className="text-xs text-muted-foreground mt-1">{t("dashboard.keysTracked")}</div>
        </Link>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        <Card className="bento-card" data-testid="property-status-card">
          <CardHeader><CardTitle className="text-lg">{t("dashboard.propertyStatus")}</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between"><div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-green-500" /><span className="text-sm">{t("dashboard.available")}</span></div><span className="font-semibold">{propsAvailable}</span></div>
              <div className="flex items-center justify-between"><div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-blue-500" /><span className="text-sm">{t("dashboard.rented")}</span></div><span className="font-semibold">{propsRented}</span></div>
              <div className="flex items-center justify-between"><div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-orange-500" /><span className="text-sm">{t("dashboard.maintenance")}</span></div><span className="font-semibold">{propsMaintenance}</span></div>
            </div>
          </CardContent>
        </Card>
        <Card className="bento-card" data-testid="unit-status-card">
          <CardHeader><CardTitle className="text-lg">{t("dashboard.unitStatus")}</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between"><div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-green-500" /><span className="text-sm">{t("dashboard.available")}</span></div><span className="font-semibold">{unitsAvailable}</span></div>
              <div className="flex items-center justify-between"><div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-blue-500" /><span className="text-sm">{t("dashboard.rented")}</span></div><span className="font-semibold">{unitsRented}</span></div>
              <div className="flex items-center justify-between"><div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-cyan-500" /><span className="text-sm">{t("dashboard.totalUnits")}</span></div><span className="font-semibold">{unitsTotal}</span></div>
            </div>
          </CardContent>
        </Card>
        <Card className="bento-card" data-testid="scheduled-services-card">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Calendar className="w-5 h-5 text-orange-500" />{t("dashboard.scheduledServices")}</CardTitle></CardHeader>
          <CardContent>
            <ScrollArea className="h-48">
              {scheduledServices.length > 0 ? (
                <div className="space-y-3">
                  {scheduledServices.slice(0, 5).map(function(service) {
                    var daysUntil = getDaysUntil(service.scheduled_date);
                    return (
                      <Link to="/services" key={service.service_id} className="block p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{service.title}</p>
                            <p className="text-xs text-muted-foreground mt-1">{service.property_name || t("common.noProperty")}</p>
                          </div>
                          {getUrgencyBadge(daysUntil)}
                        </div>
                        <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDate(service.scheduled_date)}</span>
                          {service.service_type && <Badge variant="outline" className="text-xs">{t("services.types." + service.service_type)}</Badge>}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-muted-foreground py-8">
                  <Wrench className="w-8 h-8 mb-2 opacity-50" />
                  <p className="text-sm">{t("dashboard.noScheduledServices")}</p>
                </div>
              )}
            </ScrollArea>
            {scheduledServices.length > 5 && (
              <Link to="/services" className="block text-center text-sm text-primary hover:underline mt-3">
                {t("dashboard.viewAllServices", { count: scheduledServices.length })}
              </Link>
            )}
          </CardContent>
        </Card>
        <Card className="bento-card" data-testid="upcoming-reminders-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2"><Bell className="w-5 h-5 text-purple-500" />{t("dashboard.upcomingReminders")}</CardTitle>
            </div>
            <div className="flex gap-1 mt-2">
              {[7, 14, 30].map(function(days) {
                return (
                  <Button
                    key={days}
                    variant={reminderDays === days ? "default" : "outline"}
                    size="sm"
                    className="text-xs px-2 py-1 h-7"
                    onClick={function() { setReminderDays(days); }}
                    data-testid={"reminder-days-" + days}
                  >
                    {days} {t("dashboard.days")}
                  </Button>
                );
              })}
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-40">
              {upcomingReminders.length > 0 ? (
                <div className="space-y-3">
                  {upcomingReminders.slice(0, 5).map(function(reminder) {
                    var daysUntil = getDaysUntil(reminder.next_due_date);
                    return (
                      <Link to="/reminders" key={reminder.reminder_id} className="block p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{reminder.title}</p>
                            <p className="text-xs text-muted-foreground mt-1">{reminder.reminder_type ? t("reminders.types." + reminder.reminder_type) : ""}</p>
                          </div>
                          {getUrgencyBadge(daysUntil)}
                        </div>
                        <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDate(reminder.next_due_date)}</span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-muted-foreground py-8">
                  <Bell className="w-8 h-8 mb-2 opacity-50" />
                  <p className="text-sm">{t("dashboard.noUpcomingReminders")}</p>
                </div>
              )}
            </ScrollArea>
            {upcomingReminders.length > 5 && (
              <Link to="/reminders" className="block text-center text-sm text-primary hover:underline mt-3">
                {t("dashboard.viewAllReminders", { count: upcomingReminders.length })}
              </Link>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="bento-card" data-testid="recent-activity">
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><TrendingUp className="w-5 h-5 text-primary" />{t("dashboard.recentActivity")}</CardTitle></CardHeader>
        <CardContent>
          <ScrollArea className="h-64">
            {recentActivity.length > 0 ? (
              <div className="space-y-3">
                {recentActivity.map(function(log, idx) {
                  return (
                    <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                      <Badge className={getActionColor(log.action) + " shrink-0"}>{log.action}</Badge>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm"><span className="font-medium">{log.user_name}</span> {log.action}d {log.entity_type}{log.entity_name && <span className="text-muted-foreground"> "{log.entity_name}"</span>}</p>
                        <p className="text-xs text-muted-foreground mt-1">{formatTimestamp(log.timestamp)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <div className="h-full flex items-center justify-center text-muted-foreground">{t("dashboard.noRecentActivity")}</div>}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
